// Read-only render of a Report. Lays panels out by panel.width:
// 'full' = whole row, 'half' = 2 per row, 'third' = 3 per row.
//
// Each panel gets a session-only Auto toggle: the saved default is
// `panel.autoUpdate`; the user can flip it for the current view, but
// the override doesn't persist (resets when the report is closed). A
// report-level "Auto all / Pause all" button flips every panel at once.
//
// Timeframe: a report is timeframe-aware when any panel's SQL uses the
// `{{since:..}}` placeholder (report-time.js). The toolbar picker then
// overrides every panel's window at once (session-only, like Auto), and
// line charts support drag-to-zoom into an absolute range — double-click
// (or the toolbar button) zooms back out to the pre-zoom selection.

import { useRef, useState } from 'react';
import { Panel } from './Panel.jsx';
import { I } from '../icons.jsx';
import {
  TIME_PRESETS, reportHasTimeframe, relativeRange, absoluteRange, describeRange,
} from '../../data/report-time.js';

export function ReportView({ report }) {
  // Per-panel session override. Keyed by panel id; absence means "follow
  // the saved default". Flipping toggles between true / false.
  const [autoOverrides, setAutoOverrides] = useState({});

  // Report-level timeframe override: null = each panel's own default,
  // relative = toolbar preset, absolute = drag-zoom on a line chart.
  // `preZoomRef` remembers the preset in effect before the first zoom so
  // zooming out returns there rather than to "Panel default".
  const [timeRange, setTimeRange] = useState(null);
  const preZoomRef = useRef(null);

  if (!report) return null;
  const panels = report.panels || [];
  const hasTimeframe = reportHasTimeframe(report);

  const zoomTo = (fromUs, toUs) => setTimeRange(prev => {
    if (prev?.kind !== 'absolute') preZoomRef.current = prev;
    return absoluteRange(fromUs, toUs);
  });
  const zoomOut = () => setTimeRange(r => (r?.kind === 'absolute' ? preZoomRef.current : r));
  const pickPreset = (v) => setTimeRange(v === '' ? null : relativeRange(Number(v)));

  const isAuto = (p) => (p.id in autoOverrides) ? autoOverrides[p.id] : !!p.autoUpdate;

  const toggleAuto = (panelId, savedDefault) => {
    setAutoOverrides(prev => {
      const current = (panelId in prev) ? prev[panelId] : !!savedDefault;
      return { ...prev, [panelId]: !current };
    });
  };

  // Global toggle: if every panel is already auto, pause all; else enable all.
  const allOn = panels.length > 0 && panels.every(isAuto);
  const setAll = (on) => setAutoOverrides(Object.fromEntries(panels.map(p => [p.id, on])));

  return (
    <div className="report-view">
      <div className="report-view-head" style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        {report.description && (
          <p className="report-description" style={{ margin: 0 }}>{report.description}</p>
        )}
        {hasTimeframe && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginLeft: 'auto' }}>
            <label style={{ color: 'var(--app-fg3)', textTransform: 'uppercase', letterSpacing: '0.08em', fontSize: 11 }}>
              timeframe
            </label>
            {timeRange?.kind === 'absolute' ? (
              <>
                <span className="pill cyan" title="Zoomed to an absolute range (drag on a chart to zoom further)">
                  {describeRange(timeRange)}
                </span>
                <button
                  type="button"
                  className="btn btn-ghost btn-sm"
                  title="Zoom back out to the previous timeframe"
                  onClick={zoomOut}
                >
                  <I.Close size={11} /> Reset zoom
                </button>
              </>
            ) : (
              <select
                value={timeRange ? String(timeRange.seconds) : ''}
                onChange={e => pickPreset(e.target.value)}
                title="Time window applied to every time-bounded panel"
                style={{ background: 'var(--app-surface)', border: '1px solid var(--app-border)', borderRadius: 6, padding: '3px 8px', fontSize: 12, color: 'var(--app-fg1)', cursor: 'pointer' }}
              >
                <option value="">Panel default</option>
                {TIME_PRESETS.map(p => (
                  <option key={p.seconds} value={String(p.seconds)}>Last {p.label}</option>
                ))}
              </select>
            )}
          </div>
        )}
        {panels.length > 0 && (
          <button
            type="button"
            className={'btn btn-sm ' + (allOn ? 'btn-primary' : 'btn-ghost')}
            style={{ marginLeft: 'auto' }}
            title={allOn ? 'Live-updating every panel — click to pause all' : 'Live-update every panel'}
            onClick={() => setAll(!allOn)}
          >
            <I.Refresh size={12} /> {allOn ? 'Pause all' : 'Auto all'}
          </button>
        )}
      </div>
      {panels.length === 0 ? (
        <div className="report-empty">This report has no panels yet.</div>
      ) : (
        <div className="report-grid">
          {panels.map(p => (
            <Panel
              key={p.id}
              panel={p}
              autoEffective={isAuto(p)}
              onToggleAuto={() => toggleAuto(p.id, p.autoUpdate)}
              timeRange={timeRange}
              onZoom={zoomTo}
              onZoomOut={zoomOut}
            />
          ))}
        </div>
      )}
    </div>
  );
}
